/**
 * MOCK CONTENT — 7-daagse challenge.
 *
 * Everything below is placeholder material for the first theme, "Dankbaarheid".
 * The texts are written for review, not for release, and the audio links point
 * at existing meditations/muziek/natuurgeluiden so the players actually work
 * while the real recordings are being made.
 *
 * When the challenge moves into the database, keep this shape and swap the
 * constant for a query — the page only reads `CHALLENGE`.
 */

export interface ChallengeTrack {
  /** Stable id — AudioPlayer uses it for favourites and playtime tracking. */
  id: string;
  title: string;
  subtitle: string;
  audioUrl: string;
  imageUrl: string;
  /** Shown next to the title, e.g. "5 min". */
  duration: string;
}

/** A paragraph of body copy; `heading` marks a bold sub-heading. */
export interface ChallengeParagraph {
  text: string;
  heading?: boolean;
}

/**
 * An introduction section: a title, a recording, and the text underneath it.
 * These sit between the day circles and day 1.
 */
export interface ChallengeIntroSection {
  id: string;
  title: string;
  audio: ChallengeTrack;
  body: ChallengeParagraph[];
}

/** One line someone can switch on to be reminded of during the day. */
export interface ChallengeAffirmation {
  id: string;
  text: string;
}

/** Affirmations arrive in themed groups, e.g. "Zintuigen". */
export interface ChallengeAffirmationGroup {
  title: string;
  items: ChallengeAffirmation[];
}

export interface ChallengeDay {
  day: number;
  title: string;
  /** Introductie — text and audio. */
  introduction: {
    audio: ChallengeTrack;
    body: ChallengeParagraph[];
  };
  /** Moment van Aandacht — the prompt, its audio, and the lines to pick from. */
  moment: {
    audio: ChallengeTrack;
    prompt: string;
    groups: ChallengeAffirmationGroup[];
  };
  /** Meditatie — ticking this off completes the day. */
  meditation: ChallengeTrack;
}

export interface Challenge {
  slug: string;
  theme: string;
  title: string;
  subtitle: string;
  intro: string;
  /** Read before day 1: welcome, what gratitude is, and the research. */
  introSections: ChallengeIntroSection[];
  /** Time of day the daily reminder fires, as "HH:MM" (24h, local time). */
  reminderTime: string;
  days: ChallengeDay[];
}

const IMAGES = {
  intro:
    'https://images.unsplash.com/photo-1508672019048-805c876b67e2?q=80&w=1200',
  adem: 'https://images.unsplash.com/photo-1518241353330-0f7941c2d9b5?q=80&w=1200',
  lichaam:
    'https://images.unsplash.com/photo-1545205597-3d9d02c29597?q=80&w=1200',
  kleineDingen:
    'https://images.unsplash.com/photo-1490750967868-88aa4486c946?q=80&w=1200',
  mensen:
    'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?q=80&w=1200',
  moeilijk:
    'https://images.unsplash.com/photo-1504198453319-5ce911bafcde?q=80&w=1200',
  jezelf:
    'https://images.unsplash.com/photo-1499209974431-9dddcece7f88?q=80&w=1200',
  doorgeven:
    'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=1200',
  nacht:
    'https://images.unsplash.com/photo-1507400492013-162706c8c05e?q=80&w=1200',
} as const;

// Placeholder audio — existing library material, standing in for recordings
// that don't exist yet.
const AUDIO = {
  intro:
    'https://www.dropbox.com/scl/fi/txjlmy91n0k4ewpgg9biw/HznHz-Meditaties-Op_Vakantie_in_je_Hart_Muziek.mp3?rlkey=osvztakyaw28ubpp4q2c5ynqu&dl=1',
  meditatieDana:
    'https://www.dropbox.com/scl/fi/cswy3na9jkm7aqix0f7ra/HnH_Meditaties_Aarden_Dana_128kbps.mp3?rlkey=dqh7t0nenbommv9slqxs92lfc&st=b83szl0m&dl=1',
  meditatieLeo:
    'https://www.dropbox.com/scl/fi/5tcq2eo80xv46f72y8hzl/HnH_Meditaties_Aarden_Leo.mp3?rlkey=tbnvdqeldjvoxyjoyr8bgdegt&st=6flt2mpw&dl=1',
  meditatieMuziek:
    'https://www.dropbox.com/scl/fi/gwszer7bpd3buf1gs946v/Korte_Meditatie_Aarden_Muziek.mp3?rlkey=94x6e3kiqwj5u5asj1khjn1ee&st=1mrg18gh&dl=1',
  hart: 'https://www.dropbox.com/scl/fi/gz4n9fk8gz6vuwuxb1m3b/HnH_Muziek_Activeer_Het_Hart.mp3?rlkey=fj0kml4hetkczw4rg2io2t0cg&dl=1',
  schoonmaak:
    'https://www.dropbox.com/scl/fi/1aw9yctf2kqt03cjv0z44/HznHz-Meditaties-De_Grote_Schoonmaak_Muziek.mp3?rlkey=bblehae25k228ma5m7e8j5xez&dl=1',
  lente:
    'https://www.dropbox.com/scl/fi/mass0orxffng2itv0pco7/HznHz-Meditaties-De_Lente_Muziek.mp3?rlkey=aa87f8gghslywoab50kl3vdbu&dl=1',
  oceaan:
    'https://www.dropbox.com/scl/fi/y09r2gdb28n20s8i7pwci/Adem-van-de-Oceaan.mp3?rlkey=tvt339kq18qmegb0msg7o3oll&dl=1',
  alpenweide:
    'https://www.dropbox.com/scl/fi/pakldcpljkcajs8vqg0ua/Alpenweide-in-de-Ochtend.mp3?rlkey=agwt7ikyy0k417a8r0l0paig2&dl=1',
  baarmoeder:
    'https://www.dropbox.com/scl/fi/gkfqgaui9asdoeyyfceo0/Baarmoeder.mp3?rlkey=8vyw77h6sbjcpwjx1sobmlqh8&dl=1',
  bergbeek:
    'https://www.dropbox.com/scl/fi/3it0z1llf6mo0jp0bueno/Fluisteringen-van-de-Bergbeek.mp3?rlkey=ovdhfzyddfqclfomix1sv9jvw&dl=1',
} as const;

export const CHALLENGE: Challenge = {
  slug: 'dankbaarheid',
  theme: 'Dankbaarheid',
  title: '7-Daagse Challenge',
  subtitle: 'Dankbaarheid',
  intro:
    'Zeven dagen lang neem je elke dag een paar minuten voor dankbaarheid. ' +
    "'s Ochtends een korte meditatie van vijf minuten, om 13:00 een klein " +
    'moment van aandacht, en voor het slapengaan een meditatie om de dag rustig ' +
    'af te sluiten. Je hoeft niets te presteren — je hoeft alleen even te merken ' +
    'wat er al is.',
  introSections: [
    {
      id: 'welkom',
      title: 'Welkom',
      audio: {
        id: 'challenge-dankbaarheid-welkom',
        title: 'Welkom',
        subtitle: 'Introductie',
        audioUrl: AUDIO.intro,
        imageUrl: IMAGES.intro,
        duration: '4 min',
      },
      body: [
        {
          text: 'Zeven Dagen Dankbaarheid  is een programma dat je helpt om zeven dagen lang bewust bezig te zijn met dankbaarheid. Zeven dagen lang focus je, elke dag, op een ander stukje van wat dankbaarheid kan zijn. Je kunt dit programma zo vaak herhalen als je wilt. Het zal telkens weer nieuwe inzichten brengen.',
        },
        {
          text: 'Dit programma biedt een korte meditatie om de dag te beginnen met dankbaarheid, elke dag met een ander sub-thema. Daarnaast zijn er per dag een aantal oefeningen, reflectievragen en/ of affirmaties, waarmee je aan de slag kunt. Het is mogelijk om gedurende de dag extra herinnerd te worden aan de zin of vraag van jouw keuze door deze terug te zien in je agenda.',
        },
      ],
    },
    {
      id: 'wat-is-dankbaarheid',
      title: 'Dankbaarheid, wat is dat eigenlijk?',
      audio: {
        id: 'challenge-dankbaarheid-wat-is-het',
        title: 'Dankbaarheid, wat is dat eigenlijk?',
        subtitle: 'Introductie',
        audioUrl: AUDIO.hart,
        imageUrl: IMAGES.kleineDingen,
        duration: '6 min',
      },
      body: [
        {
          text: 'Dankbaarheid… tja, wat is dat nou eigenlijk? Een gevoel? Een emotie? Een gedachte? Een “staat van zijn”? Dankbaarheid is het allemaal, en nog veel meer dan dat. Je kunt het voelen, en soms ook missen. Én, je kunt ermee werken. Want dankbaarheid is ook een tool die je je eigen kunt maken, en die je als het ware kunt trainen.',
        },
        {
          text: 'We hebben allemaal, in meer of mindere mate, geleerd dat je dankbaar moet zijn wanneer je bijvoorbeeld iets krijgt of als iemand je helpt. Maar, dankbaarheid is veel meer dan aangeleerd, beleefd gedrag. Het is een krachtige manier van denken en voelen die grote impact op je leven kan hebben. Onderzoek toont aan dat dankbaarheid niet alleen je humeur verbetert, maar ook je veerkracht en algehele welzijn vergroot.',
        },
        {
          text: 'Velen kennen de enorme kracht van dankbaarheid al. Daarnaast wijst ook steeds meer onderzoek uit dat dankbaarheid bijdraagt aan gezondheid. Vanuit allerlei kanten (Universiteiten, de psychologie, verzekeringsmaatschappijen, etc.) verschijnen artikelen over de kracht van dankbaarheid en haar positieve invloed op mentale, emotionele en fysieke gezondheid. En het mooie is, dat je dit gevoel kunt oefenen, en het daarmee groter – en dus krachtiger- kunt maken. Wanneer je dankbaarheid een bewuste plek in je dagelijks leven geeft, kan ze voor je aan het werk en kun jij gaan ervaren hoe ondersteunend dat is.  Óók als het leven even wat minder fijn verloopt. Wellicht júist dan.',
        },
        {
          text: 'Mensen die bewust oefenen met dankbaarheid, ervaren het leven veelal lichter; gemakkelijker. Niet door wat er gebeurt, maar door hoe zij naar het leven kijken of ermee omgaan.',
        },
        {
          text: 'Dankbaarheid gaat over een open houding; kijken naar “dat wat is”, zonder het te willen veranderen… “ermee zijn”, wordt ook weleens gezegd. Dat klinkt misschien een beetje zweverig en wellicht makkelijker gezegd dan gedaan. Maar je zult merken dat als je hiermee oefent, het steeds gemakkelijker wordt.',
        },
      ],
    },
    {
      id: 'psychologie',
      title: 'De psychologie van dankbaarheid',
      audio: {
        id: 'challenge-dankbaarheid-psychologie',
        title: 'De psychologie van dankbaarheid',
        subtitle: 'Introductie',
        audioUrl: AUDIO.lente,
        imageUrl: IMAGES.mensen,
        duration: '8 min',
      },
      body: [
        {
          text: 'Dankbaarheid kan het subjectief welbevinden vergroten en daarmee veerkracht versterken. Dankbaarheid vergroot een gevoel van controle of grip, draagt bij aan persoonlijke groei, heeft een positieve invloed op persoonlijke relaties en zingeving en vergroot zelfacceptatie.',
        },
        { text: '(Dankbaarheid is goed voor je | Radboud Universiteit)' },
        {
          text: 'Dankbaarheid wordt gelinkt aan een groter gevoel van geluk. Het draagt bij aan een toename van positieve emoties, fijnere relaties en zelfs betere gezondheid. Dankbaarheid leert je aandacht te richten op wat je hebt, in plaats van bezig zijn met wat mist. Met andere woorden: “Wat wel” in plaats van “Wat niet”. Dit kan leiden tot meer tevredenheid en welzijn. In het begin kan het wat gedwongen voelen om je focus te verleggen, maar door het regelmatig te oefenen wordt het vanzelf makkelijker.',
        },
        { text: 'De psychologische voordelen van dankbaarheid', heading: true },
        {
          text: 'Dankbaarheid heeft tal van psychologische voordelen. Onderzoek wijst uit dat het beoefenen van dankbaarheid kan bijdragen aan een betere mentale gezondheid, doordat negatieve emoties verminderen en positiviteit wordt bevorderd. Dankbaarheid vergroot het gevoel van eigenwaarde, betere slaap en een dieper gevoel van geluk. Het risico op depressie of angst vermindert en vaak voelen mensen zich meer verbonden met anderen.',
        },
        {
          text: 'Doordat de focus meer komt te liggen op positieve ervaringen en steunbronnen, helpt dankbaarheid om beter om te gaan met stress en tegenslagen. Dit vergroot de algehele veerkracht en het vermogen om zich aan te passen aan veranderende omstandigheden.',
        },
        { text: 'Hoe dankbaarheid je relaties kan verbeteren?', heading: true },
        {
          text: 'Dankbaarheid speelt een cruciale rol in het versterken van relaties. Met het uiten van dankbaarheid laat je anderen merken dat je hun inspanningen of aanwezigheid waardeert. Dit kan leiden tot een diepere en meer betekenisvolle verbinding. Bovendien, wanneer mensen zich gewaardeerd voelen, zijn ze meer geneigd om positief en ondersteunend te reageren, wat de relatie verder versterkt.',
        },
        {
          text: '(De kracht van dankbaarheid: hoe het beoefenen van dankbaarheid je levenskwaliteit verbetert |)',
        },
      ],
    },
    {
      id: 'oefeningen-en-tips',
      title: 'Laagdrempelige oefeningen en tips om dankbaarheid te trainen',
      audio: {
        id: 'challenge-dankbaarheid-oefeningen',
        title: 'Laagdrempelige oefeningen en tips',
        subtitle: 'Introductie',
        audioUrl: AUDIO.meditatieMuziek,
        imageUrl: IMAGES.doorgeven,
        duration: '7 min',
      },
      body: [
        { text: 'Houd een dagboek bij', heading: true },
        { text: 'Schrijf elke dag 3 dingen op waar je dankbaar voor bent.' },
        {
          text: 'Dat hoeven geen grote dingen te zijn. Sta ook juist eens stil bij de dingen die je voor lief neemt. De zon die op een regenachtige dag toch nog even doorbreekt. Het warme kopje thee in de ochtend. Dat mooie boek waar je middenin zit…',
        },
        { text: 'Spreek het uit', heading: true },
        {
          text: 'Zeg het hardop wanneer je ergens dankbaar voor bent. Een collega die je helpt, iemand die de deur voor je openhoudt, je partner die onverwachts een klus heeft geklaard, etc. Door dankbaarheid hardop uit te spreken, zet je het nóg steviger en bewuster neer.',
        },
        { text: 'Schrijf een bedankbrief', heading: true },
        {
          text: 'Schrijf eens een brief of een kaartje, waarin je iemand bedankt voor de invloed die hij/zij heeft op jouw leven. Je kunt hem opsturen of persoonlijk voorlezen. Dit blijkt ons geluksgevoel enorm te verhogen. En ook als je de brief niet verstuurt of voorleest, maakt het schrijven ervan je bewust van hele mooie dingen.',
        },
        { text: 'Oefen elke dag', heading: true },
        {
          text: '“Wat je aandacht geeft groeit”. Kijk maar naar piekeren: hoe meer je piekert, hoe vervelender je je gaat voelen en hoe groter het probleem dreigt te worden. Met dankbaarheid werkt het niet anders. Hoe meer je oefent, hoe meer je zult merken dat je anders gaat kijken; uitdagingen die lessen in zich blijken te dragen…, verdriet dat je terugbrengt naar mooie herinneringen, een frustrerende situatie die je de kracht geeft om door te zetten, etc.',
        },
        {
          text: 'Door te denken in kansen in plaats van problemen, zal je zien dat sommige dingen veel makkelijker op te lossen zijn dan je misschien dacht. Je zult ontdekken dat er veel meer is om dankbaar voor te zijn dan je voor mogelijk had gehouden. (Bron: Harvard Medical School op Waarom dankbaarheid je gelukkig maakt - Zilveren Kruis)',
        },
        { text: 'Meditatie', heading: true },
        {
          text: 'Tijdens meditatie leer je je te richten op het nu, in plaats van de toekomst of het verleden. Dit draagt bij aan een toename van tevredenheid en minder onrust. Door regelmatig te mediteren, al is het maar 5 minuutjes per dag, merk je na een tijdje dat je dit gevoel ook tijdens de rest van je dag meer en meer bij je houdt.',
        },
      ],
    },
    {
      id: 'moeite-met-dankbaarheid',
      title: 'Wat kun je doen als je moeite hebt om dankbaarheid te voelen?',
      audio: {
        id: 'challenge-dankbaarheid-moeite',
        title: 'Als dankbaarheid niet vanzelf komt',
        subtitle: 'Introductie',
        audioUrl: AUDIO.schoonmaak,
        imageUrl: IMAGES.moeilijk,
        duration: '5 min',
      },
      body: [
        {
          text: 'Het is normaal om soms moeite te hebben met het voelen van dankbaarheid, vooral in moeilijke of uitdagende tijden. Hier zijn een paar tips om je te helpen:',
        },
        {
          text: 'Kleine overwinningen vieren: Begin met het herkennen en waarderen van kleine positieve momenten in je dag. Dit kan je helpen om een dankbare mindset te ontwikkelen.',
        },
        {
          text: 'Dankbaarheidsuitdaging: Daag jezelf uit om elke dag iets nieuws te vinden om dankbaar voor te zijn. Dit kan je helpen om je focus te verleggen van het negatieve naar het positieve.',
        },
        {
          text: 'Deel en laat steun toe: Praat met een vriend, familielid of therapeut over je gevoelens. Soms kan een ander perspectief je helpen om dankbaarheid te vinden, zelfs in moeilijke situaties.',
        },
      ],
    },
  ],
  reminderTime: '13:00',
  days: [
    {
      day: 1,
      title: 'Dankbaar voor je lichaam',
      introduction: {
        audio: {
          id: 'challenge-dankbaarheid-dag1-introductie',
          title: 'Dag 1 — Introductie',
          subtitle: 'Introductie',
          audioUrl: AUDIO.meditatieLeo,
          imageUrl: IMAGES.lichaam,
          duration: '3 min',
        },
        body: [
          {
            text: 'Je lichaam draagt je de hele dag, meestal zonder dat je het opmerkt. Vandaag sta je stil bij alles wat het voor je doet.',
          },
        ],
      },
      moment: {
        audio: {
          id: 'challenge-dankbaarheid-dag1-moment',
          title: 'Dag 1 — Moment van Aandacht',
          subtitle: 'Moment van Aandacht',
          audioUrl: AUDIO.hart,
          imageUrl: IMAGES.lichaam,
          duration: '4 min',
        },
        prompt:
          'Kies een zin, vraag of oefening waar je vandaag mee aan de slag wilt gaan:',
        groups: [
          {
            title: 'Automatische functies',
            items: [
              {
                id: 'd1-g1-1',
                text: '“Mijn lichaam werkt constant voor mij, zelfs als ik het niet merk.”',
              },
              {
                id: 'd1-g1-2',
                text: '“Mijn hart klopt elke dag voor mij — ik ben dankbaar voor dit constante ritme.”',
              },
              {
                id: 'd1-g1-3',
                text: '“Met elke ademhaling krijg ik nieuwe energie of kan ik spanning loslaten.”',
              },
              {
                id: 'd1-g1-4',
                text: '“Mijn gedachten en gevoelens mogen er zijn — mijn brein helpt mij navigeren.”',
              },
              {
                id: 'd1-g1-5',
                text: '“Mijn lichaam beschermt mij, vaak zonder dat ik het doorheb.”',
              },
            ],
          },
          {
            title: 'Beweging en kracht',
            items: [
              {
                id: 'd1-g2-1',
                text: '“Mijn spieren ondersteunen mij — ze helpen me te doen wat ik wil.”',
              },
              {
                id: 'd1-g2-2',
                text: '“Mijn lichaam heeft structuur en stevigheid en daarmee draagt en beschermt het mij — daar vertrouw ik op.”',
              },
              {
                id: 'd1-g2-3',
                text: '“Mijn benen brengen mij naar waar ik wil zijn — stap voor stap.”',
              },
              {
                id: 'd1-g2-4',
                text: '“Met mijn handen kan ik geven, ontvangen, vasthouden en creëren — dat is waardevol.”',
              },
            ],
          },
          {
            title: 'Zintuigen',
            items: [
              {
                id: 'd1-g3-1',
                text: '“Ik kan zien wat belangrijk voor mij is — ik neem de wereld in mij op.”',
              },
              {
                id: 'd1-g3-2',
                text: '“Ik kan luisteren en verbinden — geluiden brengen mij dichtbij anderen.”',
              },
              {
                id: 'd1-g3-3',
                text: '“Mijn huid laat mij voelen — daardoor kan ik ervaren wat er is.”',
              },
              {
                id: 'd1-g3-4',
                text: '“Ik kan genieten van geuren en smaken — kleine momenten van plezier.”',
              },
            ],
          },
          {
            title: 'Herstel en veerkracht',
            items: [
              {
                id: 'd1-g4-1',
                text: '“Mijn lichaam helpt mij telkens weer om in balans te komen of te blijven.”',
              },
              {
                id: 'd1-g4-2',
                text: '“Mijn lichaam probeert steeds weer te herstellen — ik geef het de ruimte.”',
              },
              {
                id: 'd1-g4-3',
                text: '“Mijn lichaam geeft aan wanneer het rust nodig heeft — ik mag daarnaar luisteren.”',
              },
              {
                id: 'd1-g4-4',
                text: '“Mijn lichaam past zich aan en blijft me ondersteunen — ook als het moeilijk is.”',
              },
            ],
          },
          {
            title: 'Verbondenheid en expressie',
            items: [
              {
                id: 'd1-g5-1',
                text: '“Mijn lichaam helpt me relaties op te bouwen en te onderhouden.”',
              },
              {
                id: 'd1-g5-2',
                text: '“Mijn gezicht laat zien wat ik voel — ik mag zichtbaar zijn.”',
              },
              {
                id: 'd1-g5-3',
                text: '“Mijn stem geeft mij een plek in de wereld — ik mag mij uitspreken.”',
              },
              {
                id: 'd1-g5-4',
                text: '“Via mijn lichaam kan ik verbinden, met mezelf en anderen — dat is betekenisvol.”',
              },
            ],
          },
          {
            title: 'Dankbaarheidsmantra — innerlijke signalen',
            items: [
              {
                id: 'd1-g6-1',
                text: '“Mijn lichaam geeft signalen die me helpen om voor mezelf te zorgen.”',
              },
              {
                id: 'd1-g6-2',
                text: '“Mijn lichaam laat mij weten wat het nodig heeft — ik leer luisteren.”',
              },
              {
                id: 'd1-g6-3',
                text: '“Wat ik voel in mijn lichaam heeft betekenis — ik neem het serieus.”',
              },
              {
                id: 'd1-g6-4',
                text: '“Mijn lichaam helpt mij herkennen wat goed voor mij is.”',
              },
            ],
          },
        ],
      },
      meditation: {
        id: 'challenge-dankbaarheid-dag1-meditatie',
        title: 'Dag 1 — Meditatie',
        subtitle: 'Meditatie',
        audioUrl: AUDIO.meditatieDana,
        imageUrl: IMAGES.lichaam,
        duration: '5 min',
      },
    },
    {
      day: 2,
      title: 'Dankbaar voor je adem',
      introduction: {
        audio: {
          id: 'challenge-dankbaarheid-dag2-introductie',
          title: 'Dag 2 — Introductie',
          subtitle: 'Introductie',
          audioUrl: AUDIO.meditatieDana,
          imageUrl: IMAGES.adem,
          duration: '3 min',
        },
        body: [
          {
            text: 'Je adem is er al je hele leven, zonder dat je erom hoeft te vragen. Vandaag sta je er even bij stil.',
          },
        ],
      },
      moment: {
        audio: {
          id: 'challenge-dankbaarheid-dag2-moment',
          title: 'Dag 2 — Moment van Aandacht',
          subtitle: 'Moment van Aandacht',
          audioUrl: AUDIO.oceaan,
          imageUrl: IMAGES.adem,
          duration: '4 min',
        },
        prompt:
          'Kies een zin, vraag of oefening waar je vandaag mee aan de slag wilt gaan:',
        groups: [
          {
            title: 'Affirmaties',
            items: [
              {
                id: 'd2-g1-1',
                text: '“Mijn adem is er altijd — ik hoef er niets voor te doen.”',
              },
              {
                id: 'd2-g1-2',
                text: '“Met elke uitademing mag ik iets loslaten.”',
              },
              {
                id: 'd2-g1-3',
                text: '“Mijn adem brengt mij terug naar dit moment.”',
              },
            ],
          },
        ],
      },
      meditation: {
        id: 'challenge-dankbaarheid-dag2-meditatie',
        title: 'Dag 2 — Meditatie',
        subtitle: 'Meditatie',
        audioUrl: AUDIO.oceaan,
        imageUrl: IMAGES.adem,
        duration: '5 min',
      },
    },
    {
      day: 3,
      title: 'Dankbaar voor de kleine dingen',
      introduction: {
        audio: {
          id: 'challenge-dankbaarheid-dag3-introductie',
          title: 'Dag 3 — Introductie',
          subtitle: 'Introductie',
          audioUrl: AUDIO.meditatieMuziek,
          imageUrl: IMAGES.kleineDingen,
          duration: '3 min',
        },
        body: [
          {
            text: 'De eerste slok koffie. Licht door het raam. Dankbaarheid zit vaker in kleine dingen dan in grote.',
          },
        ],
      },
      moment: {
        audio: {
          id: 'challenge-dankbaarheid-dag3-moment',
          title: 'Dag 3 — Moment van Aandacht',
          subtitle: 'Moment van Aandacht',
          audioUrl: AUDIO.bergbeek,
          imageUrl: IMAGES.kleineDingen,
          duration: '4 min',
        },
        prompt:
          'Kies een zin, vraag of oefening waar je vandaag mee aan de slag wilt gaan:',
        groups: [
          {
            title: 'Affirmaties',
            items: [
              {
                id: 'd3-g1-1',
                text: '“Ik merk vandaag één klein ding op dat mij goed doet.”',
              },
              {
                id: 'd3-g1-2',
                text: '“Kleine momenten tellen net zo hard mee.”',
              },
              {
                id: 'd3-g1-3',
                text: '“Wat gewoon lijkt, is niet vanzelfsprekend.”',
              },
            ],
          },
        ],
      },
      meditation: {
        id: 'challenge-dankbaarheid-dag3-meditatie',
        title: 'Dag 3 — Meditatie',
        subtitle: 'Meditatie',
        audioUrl: AUDIO.bergbeek,
        imageUrl: IMAGES.kleineDingen,
        duration: '5 min',
      },
    },
    {
      day: 4,
      title: 'Dankbaar voor mensen om je heen',
      introduction: {
        audio: {
          id: 'challenge-dankbaarheid-dag4-introductie',
          title: 'Dag 4 — Introductie',
          subtitle: 'Introductie',
          audioUrl: AUDIO.hart,
          imageUrl: IMAGES.mensen,
          duration: '3 min',
        },
        body: [
          {
            text: 'Iemand deed vandaag iets voor je, hoe klein ook. Vandaag laat je dat binnenkomen.',
          },
        ],
      },
      moment: {
        audio: {
          id: 'challenge-dankbaarheid-dag4-moment',
          title: 'Dag 4 — Moment van Aandacht',
          subtitle: 'Moment van Aandacht',
          audioUrl: AUDIO.alpenweide,
          imageUrl: IMAGES.mensen,
          duration: '4 min',
        },
        prompt:
          'Kies een zin, vraag of oefening waar je vandaag mee aan de slag wilt gaan:',
        groups: [
          {
            title: 'Affirmaties',
            items: [
              {
                id: 'd4-g1-1',
                text: '“Ik laat hulp van anderen binnenkomen.”',
              },
              {
                id: 'd4-g1-2',
                text: '“Ik ben niet alleen — er zijn mensen om mij heen.”',
              },
              {
                id: 'd4-g1-3',
                text: '“Ik mag laten merken dat ik iemand waardeer.”',
              },
            ],
          },
        ],
      },
      meditation: {
        id: 'challenge-dankbaarheid-dag4-meditatie',
        title: 'Dag 4 — Meditatie',
        subtitle: 'Meditatie',
        audioUrl: AUDIO.alpenweide,
        imageUrl: IMAGES.mensen,
        duration: '5 min',
      },
    },
    {
      day: 5,
      title: 'Dankbaar voor wat moeilijk was',
      introduction: {
        audio: {
          id: 'challenge-dankbaarheid-dag5-introductie',
          title: 'Dag 5 — Introductie',
          subtitle: 'Introductie',
          audioUrl: AUDIO.schoonmaak,
          imageUrl: IMAGES.moeilijk,
          duration: '3 min',
        },
        body: [
          {
            text: 'Niet alles hoeft fijn te zijn om je iets te brengen. Vandaag kijk je met zachtheid naar wat zwaar was.',
          },
        ],
      },
      moment: {
        audio: {
          id: 'challenge-dankbaarheid-dag5-moment',
          title: 'Dag 5 — Moment van Aandacht',
          subtitle: 'Moment van Aandacht',
          audioUrl: AUDIO.oceaan,
          imageUrl: IMAGES.moeilijk,
          duration: '4 min',
        },
        prompt:
          'Kies een zin, vraag of oefening waar je vandaag mee aan de slag wilt gaan:',
        groups: [
          {
            title: 'Affirmaties',
            items: [
              {
                id: 'd5-g1-1',
                text: '“Wat zwaar was, heeft mij ook iets gebracht.”',
              },
              { id: 'd5-g1-2', text: '“Ik mag mild zijn voor mezelf.”' },
              {
                id: 'd5-g1-3',
                text: '“Ik hoef niet alles op te lossen om er vrede mee te hebben.”',
              },
            ],
          },
        ],
      },
      meditation: {
        id: 'challenge-dankbaarheid-dag5-meditatie',
        title: 'Dag 5 — Meditatie',
        subtitle: 'Meditatie',
        audioUrl: AUDIO.oceaan,
        imageUrl: IMAGES.moeilijk,
        duration: '5 min',
      },
    },
    {
      day: 6,
      title: 'Dankbaar voor jezelf',
      introduction: {
        audio: {
          id: 'challenge-dankbaarheid-dag6-introductie',
          title: 'Dag 6 — Introductie',
          subtitle: 'Introductie',
          audioUrl: AUDIO.meditatieDana,
          imageUrl: IMAGES.jezelf,
          duration: '3 min',
        },
        body: [
          {
            text: 'Dankbaarheid richt zich vaak naar buiten. Vandaag draait het naar binnen — naar jou.',
          },
        ],
      },
      moment: {
        audio: {
          id: 'challenge-dankbaarheid-dag6-moment',
          title: 'Dag 6 — Moment van Aandacht',
          subtitle: 'Moment van Aandacht',
          audioUrl: AUDIO.baarmoeder,
          imageUrl: IMAGES.jezelf,
          duration: '4 min',
        },
        prompt:
          'Kies een zin, vraag of oefening waar je vandaag mee aan de slag wilt gaan:',
        groups: [
          {
            title: 'Affirmaties',
            items: [
              {
                id: 'd6-g1-1',
                text: '“Ik mag trots zijn op wat ik vandaag heb gedaan.”',
              },
              { id: 'd6-g1-2', text: '“Ik ben goed zoals ik ben.”' },
              {
                id: 'd6-g1-3',
                text: '“Ik geef mezelf dezelfde vriendelijkheid als een goede vriend.”',
              },
            ],
          },
        ],
      },
      meditation: {
        id: 'challenge-dankbaarheid-dag6-meditatie',
        title: 'Dag 6 — Meditatie',
        subtitle: 'Meditatie',
        audioUrl: AUDIO.baarmoeder,
        imageUrl: IMAGES.jezelf,
        duration: '5 min',
      },
    },
    {
      day: 7,
      title: 'Dankbaarheid doorgeven',
      introduction: {
        audio: {
          id: 'challenge-dankbaarheid-dag7-introductie',
          title: 'Dag 7 — Introductie',
          subtitle: 'Introductie',
          audioUrl: AUDIO.lente,
          imageUrl: IMAGES.doorgeven,
          duration: '3 min',
        },
        body: [
          {
            text: 'Zeven dagen aandacht. Vandaag geef je er iets van door — een berichtje, een woord, een blik.',
          },
        ],
      },
      moment: {
        audio: {
          id: 'challenge-dankbaarheid-dag7-moment',
          title: 'Dag 7 — Moment van Aandacht',
          subtitle: 'Moment van Aandacht',
          audioUrl: AUDIO.bergbeek,
          imageUrl: IMAGES.doorgeven,
          duration: '4 min',
        },
        prompt:
          'Kies een zin, vraag of oefening waar je vandaag mee aan de slag wilt gaan:',
        groups: [
          {
            title: 'Affirmaties',
            items: [
              {
                id: 'd7-g1-1',
                text: '“Ik geef vandaag iets van mijn dankbaarheid door.”',
              },
              {
                id: 'd7-g1-2',
                text: '“Waardering uitspreken kost mij niets en geeft veel.”',
              },
              {
                id: 'd7-g1-3',
                text: '“Wat ik deze week merkte, neem ik mee.”',
              },
            ],
          },
        ],
      },
      meditation: {
        id: 'challenge-dankbaarheid-dag7-meditatie',
        title: 'Dag 7 — Meditatie',
        subtitle: 'Meditatie',
        audioUrl: AUDIO.bergbeek,
        imageUrl: IMAGES.doorgeven,
        duration: '5 min',
      },
    },
  ],
};
